<template>
  <JBLoadingSpinner v-if="loading" />
  <div v-else-if="errored" class="[ text-red ]">
    An error occurred loading the requested user.
  </div>
  <section class="[ flex flex-col gap-4 ]" v-else-if="data.customer?.preview">
    <div class="[ font-bold text-2xl ]">
      {{ data.customer.preview.firstName || "-" }}
      {{ data.customer.preview.lastName || "-" }}
    </div>

    <CustomerEntries
      :entries="data.customer.entries"
      :fields="data.callFields"
    />

    <hr class="[ my-4 ]" />

    <Tabs value="notes">
      <TabList>
        <Tab value="notes">Notes</Tab>
        <Tab value="orders">{{ ordersLabel }}</Tab>
      </TabList>
      <TabPanels class="[ mt-4 px-0 ]">
        <TabPanel value="notes">
          <CustomerCallSummaryAndNotes
            :contacts="data.customer.contactsHistory"
            :contact-notes="data.customer.customerNotes || []"
          />
        </TabPanel>
        <TabPanel value="orders">
          <CustomerOrderList
            :orders="data.orders"
            :orders-label="ordersLabel"
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </section>
</template>

<script setup lang="ts">
import { Tabs, TabList, Tab, TabPanel, TabPanels } from "primevue";
import { useApi, useApiLoader } from "@/services/api";
import { useRouter } from "vue-router";
import { computed, onMounted } from "vue";
import { CustomerDetails, Field, Order } from "@/services/api/sdk";
import JBLoadingSpinner from "@/components/ui/JBLoadingSpinner.vue";
import CustomerEntries from "@/components/customers/CustomerEntries.vue";
import CustomerCallSummaryAndNotes from "@/components/customers/CustomerCallSummaryAndNotes.vue";
import CustomerOrderList from "@/components/customers/CustomerOrderList.vue";
import { setBreadcrumbs } from "@/utils/breadcrumb.ts";
import { storeToRefs } from "pinia";
import { useClientConfig } from "@/store/client-config.ts";

definePage({
  meta: {
    title: "Customer profile",
  },
});

const api = useApi();
const { tenantGlobalConfig } = storeToRefs(useClientConfig());
const { resolve, currentRoute } = useRouter();
const customerId = parseInt(currentRoute.value.params?.id!.toString(), 10);

const ordersConfig = computed(() =>
  tenantGlobalConfig.value.settings?.features?.find((f) => f.key === "Orders"),
);
const ordersLabel = computed<string>(
  () => ordersConfig.value?.label || "Orders",
);

const { load, loading, data, errored } = useApiLoader<{
  customer: CustomerDetails;
  orders: Order[];
  callFields: Field[];
}>(
  async () => {
    const response = await Promise.all([
      api.getCustomerDetails({
        customerId,
      }),
      api.getCustomerOrders({
        customerId,
      }),
      api.getContactFields({
        contactId: customerId,
      }),
    ]);

    setBreadcrumbs({
      crumbs: [resolve("/customers")],
      trailingTitle: `${response[0].preview?.firstName} ${response[0].preview?.lastName}`,
    });

    return {
      customer: response[0],
      orders: response[1].data,
      callFields: response[2],
    };
  },
  {
    customer: {},
    orders: [],
    callFields: [],
  },
);

onMounted(() => {
  load();
});
</script>
